import { useState } from 'react';
import DateInput from '@/src/components/date-input';
import { motion } from 'motion/react';
import { RefreshCw, Upload, Target, TrendingUp, TrendingDown, Wallet, Receipt, AlertTriangle, Clock, PieChart, ShoppingBag, CreditCard } from 'lucide-react';
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend, Cell, ComposedChart, Line } from 'recharts';
import { useAppColors } from '@/src/lib/theme';
import { formatMoney, timeAgo } from '@/src/lib/format';
import { useOwnerMetrics, type MetricsPeriod, type DateRange } from '@/src/hooks/useOwnerMetrics';
import SalesImportModal from './sales-import-modal';
import ForecastModal from './forecast-modal';
import NumbersCogs from './numbers-cogs';
import { Kpi, Section, Empty, gridCols, GOLD, RED } from './numbers-ui';
import DayView from './numbers-day';


type View = MetricsPeriod | 'day';
const PERIODS: Array<{ key: View; label: string }> = [
  { key: 'day', label: 'Day' },
  { key: 'week', label: 'This week' },
  { key: 'last_week', label: 'Last week' },
  { key: 'month', label: 'This month' },
  { key: 'last_month', label: 'Last month' },
  { key: '30d', label: '30 days' },
  { key: 'custom', label: 'Custom' },
];
const VIEWS = PERIODS.map((p) => p.key);

const localIso = (d = new Date()) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
const shiftIso = (iso: string, n: number) => { const d = new Date(`${iso}T12:00:00`); d.setDate(d.getDate() + n); return localIso(d); };
/** % change, or null when there is nothing to compare against. */
const pctChange = (cur: number | null | undefined, prev: number | null | undefined) =>
  cur == null || prev == null || prev <= 0 ? null : Math.round(((cur - prev) / prev) * 100);
/** Axis label for a date, depending on how many days are on the chart. */
const tickFor = (n: number) => (iso: string) => {
  const d = new Date(`${iso}T12:00:00`);
  if (n <= 7) return d.toLocaleDateString('en-AU', { weekday: 'short' });
  if (n <= 31) return String(d.getDate());
  return d.toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
};
const fullDate = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-AU', { weekday: 'short', day: 'numeric', month: 'short', year: 'numeric' });

interface Props {
  venueId: string;
  userRole: string;
  userId?: string;
  /** Inside Financials: no page title, no Forecast button (it's a tab there). */
  embedded?: boolean;
}

export default function NumbersPanel({ venueId, userRole, userId, embedded = false }: Props) {
  const c = useAppColors();
  const [view, setView] = useState<View>(() => { try { const v = localStorage.getItem('opsalo:numbers-view') as View; return VIEWS.includes(v) ? v : 'month'; } catch { return 'month'; } });
  const period: MetricsPeriod = view === 'day' ? 'month' : view;
  // Custom range: what's typed in the pickers (draft) vs what's been applied (range).
  const [range, setRange] = useState<DateRange>(() => {
    try { const r = JSON.parse(localStorage.getItem('opsalo:numbers-range') ?? 'null'); if (r?.from && r?.to) return r; } catch { /* ignore */ }
    return { from: shiftIso(localIso(), -6), to: localIso() };
  });
  const [draft, setDraft] = useState<DateRange>(range);
  const draftOk = !!draft.from && !!draft.to && draft.from <= draft.to;
  const applyRange = () => {
    if (!draftOk) return;
    setRange(draft);
    try { localStorage.setItem('opsalo:numbers-range', JSON.stringify(draft)); } catch { /* ignore */ }
  };
  const [dayKey, setDayKey] = useState(0);
  const pick = (v: View) => { setView(v); try { localStorage.setItem('opsalo:numbers-view', v); } catch { /* ignore */ } };
  const [importOpen, setImportOpen] = useState(false);
  const [forecastOpen, setForecastOpen] = useState(false);
  const canSee = userRole === 'owner' || userRole === 'manager';
  const { data, loading, error, refresh } = useOwnerMetrics(venueId, period, canSee && view !== 'day', range);

  if (!canSee) {
    return <p className="p-8 text-sm" style={{ color: c.textDim }}>Numbers are available to owners and managers.</p>;
  }

  const x = data?.xero;
  const d = data?.deputy;
  const sq = data?.square;
  const xeroOn = !!x?.connected && !x.error;
  const deputyOn = !!d?.connected && !d.error;
  const squareOn = !!sq?.connected && !sq.error;
  // Sales come from Square when connected, otherwise from OrderMate (imported CSV / nightly report).
  const isOm = sq?.source === 'ordermate';
  const posName = isOm ? 'OrderMate' : 'Square';
  const todayIso = localIso();
  const cmp = data?.compare;
  const cmpLabel = cmp?.label ?? 'vs previous';
  const delta = (cur: number | null | undefined, prev: number | null | undefined, invert = false) => {
    const pct = pctChange(cur, prev);
    return pct == null ? null : { pct, label: cmpLabel, invert };
  };
  const upcoming = period === 'week' || period === 'month';
  // Day-by-day series for the chosen range, with last period's sales lined up day for day.
  const salesSeries = (sq?.series ?? sq?.byDay?.map((b) => ({ date: b.date, sales: b.sales, orders: 0 })) ?? [])
    .map((r, i) => ({ ...r, prev: sq?.prevSeries?.[i]?.sales ?? null }));
  const hoursSeries = d?.series ?? d?.byDay?.map((b) => ({ date: b.date, hours: b.hours, cost: 0 })) ?? [];
  const nDays = Math.max(salesSeries.length, hoursSeries.length, 1);
  const xTick = tickFor(nDays);
  const hasPrev = salesSeries.some((r) => (r.prev ?? 0) > 0);
  // How much of the range actually has data, so a part-filled range isn't read as a full one.
  const shortD = (iso: string) => new Date(`${iso}T12:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' });
  const covNote = (cv?: { days: number; daysWithData: number; first: string | null; last: string | null }) =>
    cv && cv.daysWithData > 0 && cv.daysWithData < cv.days && cv.first && cv.last
      ? `Data for ${cv.daysWithData} of ${cv.days} days only (${cv.first === cv.last ? shortD(cv.first) : `${shortD(cv.first)} – ${shortD(cv.last)}`})`
      : null;
  const noSales = !!sq?.coverage && sq.coverage.daysWithData === 0;
  const noShifts = !!d?.coverage && d.coverage.daysWithData === 0;
  const salesCovNote = covNote(sq?.coverage);
  const shiftsCovNote = covNote(d?.coverage);
  const busy = loading && !data;

  const tooltipStyle = { background: c.bg, border: `1px solid ${c.border}`, borderRadius: 12, color: c.text, fontSize: 12 };
  const axisTick = { fill: c.textFaint, fontSize: 11 };

  const anyOn = squareOn || xeroOn || deputyOn;
  const showItems = squareOn && !isOm; // OrderMate item sales aren't imported yet
  const failing = [x?.connected && x.error && 'Xero', d?.connected && d.error && 'Deputy', sq?.connected && sq.error && posName]
    .filter(Boolean) as string[];
  const missing = [!xeroOn && !x?.connected && 'Xero', !deputyOn && !d?.connected && 'Deputy'].filter(Boolean) as string[];
  const missingWhat = [!xeroOn && !x?.connected && 'profit and bills', !deputyOn && !d?.connected && 'labour'].filter(Boolean).join(' and ');
  const openIntegrations = () => window.dispatchEvent(new CustomEvent('opsalo:navigate', { detail: { page: 'integrations' } }));

  const labourSub = data?.labourPct != null
    ? `Labour cost ÷ ${data.labourPctSource === 'square' ? 'Square sales' : data.labourPctSource === 'ordermate' ? 'OrderMate sales' : 'Xero revenue'}`
    : !deputyOn ? 'Connect Deputy' : !(squareOn || xeroOn) ? 'Add sales (Square, OrderMate or Xero)' : d?.cost != null ? 'No worked shifts yet' : 'Add pay rates in Deputy';

  type K = React.ComponentProps<typeof Kpi>;
  const kpis: K[] = [];
  if (squareOn) {
    kpis.push({ label: 'Sales', icon: <ShoppingBag size={15} />, value: noSales ? 'No data' : formatMoney(sq?.sales),
      sub: noSales ? `No ${posName} sales for these dates` : salesCovNote ?? `${data?.period.label} · ${posName}, ex GST`,
      valueColor: noSales ? c.textFaint : undefined, delta: noSales || salesCovNote ? null : delta(sq?.sales, cmp?.sales) });
    kpis.push({ label: isOm ? 'Covers' : 'Orders', icon: <CreditCard size={15} />, value: noSales ? 'No data' : String(sq?.orders ?? 0),
      sub: noSales ? 'Nothing recorded' : `Avg ${formatMoney(sq?.avgOrder)}${isOm ? ' per head' : ''}${salesCovNote ? ` · ${sq?.coverage?.daysWithData} days` : ''}`,
      valueColor: noSales ? c.textFaint : undefined, delta: noSales || salesCovNote ? null : delta(sq?.orders, cmp?.orders) });
  }
  if (xeroOn) {
    kpis.push({ label: 'Revenue', icon: <TrendingUp size={15} />, value: formatMoney(x?.revenue), sub: `${data?.period.label} · Xero` });
    kpis.push({ label: 'Expenses', icon: <Receipt size={15} />, value: formatMoney(x?.expenses), sub: 'Cost of sales + operating' });
    kpis.push({ label: 'Net profit', icon: (x?.net ?? 0) >= 0 ? <TrendingUp size={15} /> : <TrendingDown size={15} />, value: formatMoney(x?.net), sub: 'Revenue − expenses', valueColor: (x?.net ?? 0) >= 0 ? c.accentSoft : RED });
  }
  if (deputyOn) {
    kpis.push({ label: 'Labour cost', icon: <Wallet size={15} />, value: noShifts ? 'No data' : d?.cost != null ? formatMoney(d.cost) : '—',
      sub: noShifts ? 'No shifts in Deputy for these dates' : d?.cost != null ? shiftsCovNote ?? (upcoming ? 'Rostered, incl. upcoming' : 'Rostered') : 'Add pay rates in Deputy',
      valueColor: noShifts ? c.textFaint : undefined, delta: noShifts || shiftsCovNote ? null : delta(d?.costToDate, cmp?.labourCost, true) });
    if (squareOn || xeroOn) {
      const pts = data?.labourPct != null && cmp?.labourPct != null ? Math.round((data.labourPct - cmp.labourPct) * 10) / 10 : null;
      const pctMissing = noShifts || noSales;
      kpis.push({ label: 'Labour %', icon: <PieChart size={15} />, value: pctMissing ? 'No data' : data?.labourPct != null ? `${data.labourPct}%` : '—',
        sub: pctMissing ? `Needs ${noSales ? 'sales' : 'shifts'} for these dates` : labourSub, valueColor: pctMissing ? c.textFaint : undefined,
        delta: pts == null || pctMissing || salesCovNote || shiftsCovNote ? null : { pct: pts, label: cmpLabel, invert: true, unit: ' pts' } });
    }
    kpis.push({ label: 'Hours rostered', icon: <Clock size={15} />, value: noShifts ? 'No data' : `${d?.hours ?? 0}h`,
      sub: noShifts ? 'No shifts in Deputy for these dates' : shiftsCovNote ?? (upcoming ? 'Rostered, incl. upcoming' : 'Rostered'),
      valueColor: noShifts ? c.textFaint : undefined, delta: noShifts || shiftsCovNote ? null : delta(d?.hoursToDate, cmp?.hours, true) });
  }
  if (xeroOn) {
    kpis.push({ label: 'Unpaid bills', icon: <Receipt size={15} />, value: formatMoney(x?.unpaid?.total ?? 0), sub: `${x?.unpaid?.count ?? 0} bills` });
    kpis.push({ label: 'Overdue bills', icon: <AlertTriangle size={15} />, value: formatMoney(x?.overdue?.total ?? 0), sub: `${x?.overdue?.count ?? 0} bills`, valueColor: (x?.overdue?.count ?? 0) > 0 ? RED : undefined });
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.35, ease: [0.23, 1, 0.32, 1] }}
      className={`max-w-6xl mx-auto px-4 sm:px-8 ${embedded ? 'py-5' : 'py-8'} space-y-6`}
    >
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
        {!embedded ? (
        <div>
          <h1 className="text-3xl font-semibold" style={{ color: c.text }}>Numbers</h1>
          <p className="text-sm mt-1" style={{ color: c.textDim }}>Sales, costs and labour for your venue.</p>
        </div>
        ) : <div />}
        <div className="flex items-center gap-2 flex-wrap">
          <div className="flex rounded-xl p-1 overflow-x-auto max-w-full scrollbar-hide" style={{ background: c.iconBg }}>
            {PERIODS.map((p) => (
              <button key={p.key} onClick={() => pick(p.key)}
                className="px-3 h-8 rounded-lg text-xs font-semibold transition-colors whitespace-nowrap shrink-0"
                style={view === p.key ? { background: c.bg, color: c.text, boxShadow: '0 1px 2px rgba(0,0,0,0.08)' } : { color: c.textDim }}>
                {p.label}
              </button>
            ))}
          </div>
          {!embedded && <button onClick={() => setForecastOpen(true)}
            className="h-10 px-3 rounded-xl flex items-center gap-2 text-xs font-semibold"
            style={{ background: c.iconBg, color: c.textDim }} title="Plan bookings, covers and spend per head for a week">
            <Target size={14} /> Forecast
          </button>}
          {userId && (
            <button onClick={() => setImportOpen(true)}
              className="h-10 px-3 rounded-xl flex items-center gap-2 text-xs font-semibold"
              style={{ background: c.iconBg, color: c.textDim }} title="Import sales history from an OrderMate CSV">
              <Upload size={14} /> Import sales
            </button>
          )}
          <button onClick={() => (view === 'day' ? setDayKey((k) => k + 1) : void refresh())} disabled={loading && view !== 'day'}
            className="h-10 w-10 rounded-xl flex items-center justify-center disabled:opacity-50"
            style={{ background: c.iconBg, color: c.textDim }} aria-label="Refresh">
            <RefreshCw size={15} className={loading && view !== 'day' ? 'animate-spin' : ''} />
          </button>
        </div>
      </div>

      {view === 'custom' && (
        <div className="flex items-end gap-2 flex-wrap">
          <label className="text-[11px] font-semibold" style={{ color: c.textDim }}>From
            <DateInput value={draft.from} max={draft.to || undefined} onChange={(v) => setDraft((r) => ({ ...r, from: v }))} ariaLabel="From"
              className="flex mt-1 h-10 px-3 rounded-xl text-sm w-40" style={{ background: c.iconBg, color: c.text, border: `1px solid ${c.border}` }} />
          </label>
          <label className="text-[11px] font-semibold" style={{ color: c.textDim }}>To
            <DateInput value={draft.to} min={draft.from || undefined} onChange={(v) => setDraft((r) => ({ ...r, to: v }))} ariaLabel="To"
              className="flex mt-1 h-10 px-3 rounded-xl text-sm w-40" style={{ background: c.iconBg, color: c.text, border: `1px solid ${c.border}` }} />
          </label>
          <button onClick={applyRange} disabled={!draftOk || (draft.from === range.from && draft.to === range.to)}
            className="h-10 px-4 rounded-xl text-xs font-semibold disabled:opacity-40" style={{ background: c.accent, color: '#fff' }}>
            Show
          </button>
          {!draftOk && draft.from && draft.to && <span className="text-xs pb-3" style={{ color: RED }}>Start date must be before end date.</span>}
        </div>
      )}

      {view === 'day' ? <DayView venueId={venueId} refreshKey={dayKey} /> : <>
      {error && <p className="text-sm" style={{ color: RED }}>{error}</p>}

      {/* Number cards: only for sources that are connected */}
      {busy ? (
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
          {[0, 1, 2, 3, 4].map((i) => <Kpi key={i} label="" icon={null} value="" loading />)}
        </div>
      ) : kpis.length > 0 && (
        <div className={`grid gap-3 ${gridCols(kpis.length)}`}>
          {kpis.map((k) => <Kpi key={k.label} {...k} />)}
        </div>
      )}
      {!busy && data?.period && <NumbersCogs from={data.period.from} to={data.period.to} label={data.period.label} />}

      {!busy && !anyOn && (
        <section className="rounded-2xl p-8 text-center" style={{ background: c.bgElevated, border: `1px solid ${c.border}` }}>
          <h2 className="text-base font-semibold" style={{ color: c.text }}>No numbers yet</h2>
          <p className="text-sm mt-1 max-w-md mx-auto" style={{ color: c.textDim }}>
            Import your sales or connect your systems and your numbers will show up here.
          </p>
          <div className="flex items-center justify-center gap-2 mt-4 flex-wrap">
            {userId && (
              <button onClick={() => setImportOpen(true)} className="h-9 px-4 rounded-xl text-xs font-semibold"
                style={{ background: c.accent, color: '#fff' }}>Import sales</button>
            )}
            <button onClick={openIntegrations} className="h-9 px-4 rounded-xl text-xs font-semibold"
              style={{ background: c.iconBg, color: c.text }}>Open integrations</button>
          </div>
        </section>
      )}

      {/* Revenue vs expenses */}
      {xeroOn && (
      <Section title="Revenue vs expenses" note="Last 6 months · Xero">
        {(
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={x?.trend ?? []} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                <CartesianGrid vertical={false} stroke={c.border} />
                <XAxis dataKey="month" tick={axisTick} axisLine={false} tickLine={false} />
                <YAxis tick={axisTick} axisLine={false} tickLine={false} width={56} tickFormatter={(v: number) => formatMoney(v, { compact: true })} />
                <Tooltip contentStyle={tooltipStyle} itemStyle={{ color: c.text }} labelStyle={{ color: c.text }} cursor={{ fill: c.iconBg }} formatter={(v, name) => [formatMoney(Number(v)), String(name)]} />
                <Legend wrapperStyle={{ fontSize: 12, color: c.textDim }} />
                <Bar dataKey="revenue" name="Revenue" fill={c.accent} radius={[6, 6, 0, 0]} />
                <Bar dataKey="expenses" name="Expenses" fill={GOLD} radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Section>
      )}

      {/* Sales by day vs labour hours */}
      {(squareOn || deputyOn) && (
      <div className={`grid grid-cols-1 gap-6 ${squareOn && deputyOn ? 'lg:grid-cols-2' : ''}`}>
        {squareOn && (
        <Section title="Sales by day" note={`${data?.period.label ?? ''} · ${posName}, ex GST`}>
          {noSales ? <Empty text={`No ${posName} sales for these dates.`} /> : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={salesSeries} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={c.border} />
                  <XAxis dataKey="date" tick={axisTick} axisLine={false} tickLine={false} tickFormatter={xTick} minTickGap={8} />
                  <YAxis tick={axisTick} axisLine={false} tickLine={false} width={52} tickFormatter={(v: number) => formatMoney(v, { compact: true })} />
                  <Tooltip contentStyle={tooltipStyle} itemStyle={{ color: c.text }} labelStyle={{ color: c.text }} cursor={{ fill: c.iconBg }}
                    labelFormatter={(l) => fullDate(String(l))}
                    formatter={(v, name) => [formatMoney(Number(v)), name === 'prev' ? `Same day, ${cmpLabel.replace(/^vs /, '')}` : 'Sales']} />
                  <Bar dataKey="sales" radius={[nDays > 45 ? 2 : 6, nDays > 45 ? 2 : 6, 0, 0]}>
                    {salesSeries.map((b) => <Cell key={b.date} fill={b.date === todayIso ? c.accent : c.accentBorder} />)}
                  </Bar>
                  {hasPrev && <Line dataKey="prev" type="monotone" stroke={GOLD} strokeWidth={2} strokeDasharray="4 4" dot={false} />}
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          )}
          {!noSales && salesCovNote && <p className="text-[11px] mt-2" style={{ color: c.textFaint }}>{salesCovNote}. Days without a bar have no sales recorded.</p>}
          {!noSales && hasPrev && (
            <p className="text-[11px] mt-2 flex items-center gap-2" style={{ color: c.textFaint }}>
              <span className="inline-block w-4 border-t-2 border-dashed" style={{ borderColor: GOLD }} /> {cmpLabel.replace(/^vs /, '').replace(/^./, (m) => m.toUpperCase())}
            </p>
          )}
        </Section>
        )}

        {deputyOn && (
        <Section title="Labour hours" note={`${data?.period.label ?? ''} · Deputy`}>
          {noShifts ? <Empty text="No shifts in Deputy for these dates." /> : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={hoursSeries} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
                  <CartesianGrid vertical={false} stroke={c.border} />
                  <XAxis dataKey="date" tick={axisTick} axisLine={false} tickLine={false} tickFormatter={xTick} minTickGap={8} />
                  <YAxis tick={axisTick} axisLine={false} tickLine={false} width={32} />
                  <Tooltip contentStyle={tooltipStyle} itemStyle={{ color: c.text }} labelStyle={{ color: c.text }} cursor={{ fill: c.iconBg }} labelFormatter={(l) => fullDate(String(l))} formatter={(v) => [`${Number(v)}h`, 'Hours']} />
                  <Bar dataKey="hours" radius={[nDays > 45 ? 2 : 6, nDays > 45 ? 2 : 6, 0, 0]}>
                    {hoursSeries.map((b) => <Cell key={b.date} fill={b.date === todayIso ? GOLD : 'rgba(232,160,32,0.35)'} />)}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Section>
        )}
      </div>
      )}

      {/* Top items vs top suppliers */}
      {(showItems || xeroOn) && (
      <div className={`grid grid-cols-1 gap-6 ${showItems && xeroOn ? 'lg:grid-cols-2' : ''}`}>
        {showItems && (
        <Section title="Top items" note={`${data?.period.label ?? ''} · ${posName}`}>
          {(sq?.topItems ?? []).length === 0 ? <Empty text="No sales in this period." /> : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={sq?.topItems ?? []} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid horizontal={false} stroke={c.border} />
                  <XAxis type="number" tick={axisTick} axisLine={false} tickLine={false} tickFormatter={(v: number) => formatMoney(v, { compact: true })} />
                  <YAxis type="category" dataKey="name" tick={axisTick} axisLine={false} tickLine={false} width={110} />
                  <Tooltip contentStyle={tooltipStyle} itemStyle={{ color: c.text }} labelStyle={{ color: c.text }} cursor={{ fill: c.iconBg }} formatter={(v) => [formatMoney(Number(v)), 'Sales']} />
                  <Bar dataKey="revenue" fill={c.accent} radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Section>
        )}

        {xeroOn && (
        <Section title="Top suppliers" note={`${data?.period.label ?? ''} · Xero`}>
          {(x?.topSuppliers ?? []).length === 0 ? <Empty text="No supplier bills in this period." /> : (
            <div className="h-56">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={x?.topSuppliers ?? []} layout="vertical" margin={{ top: 4, right: 16, left: 0, bottom: 0 }}>
                  <CartesianGrid horizontal={false} stroke={c.border} />
                  <XAxis type="number" tick={axisTick} axisLine={false} tickLine={false} tickFormatter={(v: number) => formatMoney(v, { compact: true })} />
                  <YAxis type="category" dataKey="name" tick={axisTick} axisLine={false} tickLine={false} width={110} />
                  <Tooltip contentStyle={tooltipStyle} itemStyle={{ color: c.text }} labelStyle={{ color: c.text }} cursor={{ fill: c.iconBg }} formatter={(v) => [formatMoney(Number(v)), 'Spend']} />
                  <Bar dataKey="total" fill={GOLD} radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Section>
        )}
      </div>
      )}

      {/* Sources, plus problems and what else could be connected */}
      {!busy && failing.length > 0 && (
        <p className="text-xs" style={{ color: RED }}>
          Couldn't load {failing.join(' and ')} right now. Their numbers are hidden until it reconnects.{' '}
          <button onClick={openIntegrations} className="underline">Check integrations</button>
        </p>
      )}
      <p className="text-[11px]" style={{ color: c.textFaint }}>
        {squareOn && <>Sales from {posName} (net of GST and tips){isOm && sq?.lastDate ? `, up to ${new Date(`${sq.lastDate}T00:00:00`).toLocaleDateString('en-AU', { day: 'numeric', month: 'short' })}` : ''}. </>}
        {xeroOn && <>Finance from Xero{x?.org ? ` (${x.org})` : ''}. </>}
        {deputyOn && <>Roster from Deputy{d?.lastSynced ? `, synced ${timeAgo(d.lastSynced)}` : ''}. </>}
        {data?.computedAt && <>Updated {timeAgo(data.computedAt)}.</>}
        {anyOn && missing.length > 0 && (
          <> <button onClick={openIntegrations} className="underline">Connect {missing.join(' or ')}</button> to see {missingWhat}.</>
        )}
      </p>
      </>}
      <ForecastModal isOpen={forecastOpen} onClose={() => setForecastOpen(false)} venueId={venueId} userId={userId} />
      {userId && <SalesImportModal isOpen={importOpen} onClose={() => setImportOpen(false)} venueId={venueId} userId={userId} onImported={() => void refresh()} />}
    </motion.div>
  );
}
